/**
 * <EditBlock> — GitHub-style side-by-side file-edit diff. Renders the
 * `lx_edit` and `lx_doc_patch` tool_use blocks as a two-column diff
 * card with removed lines on the LEFT (red tint) and added lines on
 * the RIGHT (green tint) — matching the visual the user shared as the
 * canonical reference.
 *
 * Wire shapes accepted:
 *   - `lx_edit`     → input: { path, oldString, newString }
 *   - `lx_doc_patch`→ input: { docId, previousHash, edits: [{startLine, endLine, replacement}] }
 *
 * v1 limitations (intentional, see ai-docs/lexiun-chat-content-model-
 * plan.md §3a + §6 "Diff polish"):
 *   - No syntax highlighting (Shiki/Prism is a followup).
 *   - No per-word inline-diff inside changed lines (per-line only).
 *   - No "show all" expander on huge diffs (renders all lines).
 *   - lx_doc_patch renders all edits as a single concatenated hunk
 *     (one diff per edit is a followup).
 *
 * Selectors are stable contract for the e2e spec:
 *   data-testid="edit-block"      — root
 *   data-tool-name="<lx_edit|lx_doc_patch>"  — which tool fired
 */
import { diffLines } from "diff";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

interface EditBlockProps {
  block: ToolUseBlockType;
}

interface DiffRow {
  /** Line number in the source (left=before; right=after). undefined =
   *  alignment-pad row (the other side has content here). */
  lineNum?: number;
  text?: string;
  /** Visual hint for the row's background — only one side is set per
   *  row; the other gets a blank pad. */
  variant: "removed" | "added" | "unchanged" | "pad";
}

interface AlignedDiff {
  leftRows: DiffRow[];
  rightRows: DiffRow[];
  addedCount: number;
  removedCount: number;
}

/** Walk the change list and produce aligned left/right columns. A
 *  unchanged hunk emits the same lines on both sides; a removed/added
 *  pair (Myers diff usually adjacent) emits removed on the left + a
 *  pad on the right (for added-only) or vice-versa. Simpler than true
 *  side-by-side diff alignment but good enough for v1. */
function alignDiff(oldText: string, newText: string): AlignedDiff {
  const changes = diffLines(oldText, newText);
  const leftRows: DiffRow[] = [];
  const rightRows: DiffRow[] = [];
  let leftLine = 1;
  let rightLine = 1;
  let addedCount = 0;
  let removedCount = 0;

  function pushRow(side: "left" | "right", row: DiffRow) {
    if (side === "left") leftRows.push(row);
    else rightRows.push(row);
  }

  for (const change of changes) {
    const lines = change.value.replace(/\n$/, "").split("\n");
    if (change.added) {
      for (const line of lines) {
        // Pad the left to align with the added line on the right.
        pushRow("left", { variant: "pad" });
        pushRow("right", { variant: "added", lineNum: rightLine++, text: line });
        addedCount++;
      }
    } else if (change.removed) {
      for (const line of lines) {
        pushRow("left", { variant: "removed", lineNum: leftLine++, text: line });
        pushRow("right", { variant: "pad" });
        removedCount++;
      }
    } else {
      // Unchanged — appears on both sides at the same offset.
      for (const line of lines) {
        pushRow("left", { variant: "unchanged", lineNum: leftLine++, text: line });
        pushRow("right", { variant: "unchanged", lineNum: rightLine++, text: line });
      }
    }
  }

  return { leftRows, rightRows, addedCount, removedCount };
}

function rowClass(variant: DiffRow["variant"]): string {
  switch (variant) {
    case "removed":
      return "bg-red-50/40 dark:bg-red-900/15 text-coffee-900 dark:text-cream-100";
    case "added":
      return "bg-green-50/40 dark:bg-green-900/15 text-coffee-900 dark:text-cream-100";
    case "unchanged":
      return "text-coffee-600 dark:text-cream-400";
    case "pad":
      return "bg-cream-100/20 dark:bg-coffee-900/10";
  }
}

function DiffColumn({ rows, side }: { rows: DiffRow[]; side: "left" | "right" }) {
  return (
    <div
      data-testid={side === "left" ? "edit-block-left" : "edit-block-right"}
      className="overflow-x-auto"
    >
      {rows.map((row, i) => (
        <div
          key={i}
          className={`flex items-start px-2 ${rowClass(row.variant)}`}
        >
          <span className="mr-2 inline-block w-8 shrink-0 select-none text-right text-coffee-500 dark:text-cream-500">
            {row.lineNum ?? ""}
          </span>
          <span className="whitespace-pre-wrap break-words">{row.text ?? " "}</span>
        </div>
      ))}
    </div>
  );
}

/** Extract the {path, oldString, newString} from an lx_edit block,
 *  with defensive fallbacks for the underspecified field names some
 *  variants of the tool emit. */
function readLxEditInput(input: unknown): {
  path: string;
  oldString: string;
  newString: string;
} | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Record<string, unknown>;
  const path = typeof r.path === "string" ? r.path : "";
  const oldString =
    typeof r.oldString === "string"
      ? r.oldString
      : typeof r.old_string === "string"
        ? r.old_string
        : "";
  const newString =
    typeof r.newString === "string"
      ? r.newString
      : typeof r.new_string === "string"
        ? r.new_string
        : "";
  if (!path) return null;
  return { path, oldString, newString };
}

/** lx_doc_patch carries a `docId` + an array of `edits`; v1 concatenates
 *  all replacement bodies into a single new-side string and computes
 *  one diff over the whole change. Per-edit hunks is a followup. */
function readLxDocPatchInput(input: unknown): {
  path: string;
  oldString: string;
  newString: string;
} | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Record<string, unknown>;
  const docId = typeof r.docId === "string" ? r.docId : "";
  if (!docId) return null;
  const edits = Array.isArray(r.edits) ? r.edits : [];
  // The patch tool only carries the REPLACEMENT side; the original
  // line range is referenced numerically but not embedded in the
  // call. v1: render the replacement text as the "new" side and
  // surface the line ranges as the "old" side placeholder.
  const oldLines: string[] = [];
  const newLines: string[] = [];
  for (const e of edits) {
    if (!e || typeof e !== "object") continue;
    const er = e as { startLine?: unknown; endLine?: unknown; replacement?: unknown };
    if (typeof er.startLine === "number" && typeof er.endLine === "number") {
      oldLines.push(`<lines ${er.startLine}–${er.endLine}>`);
    }
    if (typeof er.replacement === "string") {
      newLines.push(er.replacement);
    }
  }
  return {
    path: docId,
    oldString: oldLines.join("\n"),
    newString: newLines.join("\n"),
  };
}

export function EditBlock({ block }: EditBlockProps) {
  let parsed: { path: string; oldString: string; newString: string } | null = null;
  if (block.name === "lx_edit") parsed = readLxEditInput(block.input);
  else if (block.name === "lx_doc_patch") parsed = readLxDocPatchInput(block.input);

  if (!parsed) {
    // Couldn't parse the input — render the generic collapsed-tool
    // fallback rather than show nothing. Visibility over silence.
    return (
      <div
        data-testid="edit-block"
        data-tool-name={block.name}
        data-parse-failed="true"
        className="my-1 rounded-md border border-red-300 bg-red-50/30 px-3 py-2 text-body-sm text-red-700 dark:border-red-700 dark:bg-red-900/15 dark:text-red-300"
      >
        Edit block: could not parse <code className="font-mono">{block.name}</code> input.
      </div>
    );
  }

  const { path, oldString, newString } = parsed;
  const { leftRows, rightRows, addedCount, removedCount } = alignDiff(
    oldString,
    newString,
  );

  return (
    <div
      data-testid="edit-block"
      data-tool-name={block.name}
      className="my-2 overflow-hidden rounded-md border border-cream-300 bg-cream-100/20 dark:border-coffee-700 dark:bg-coffee-900/30"
    >
      <div className="flex items-center justify-between border-b border-cream-300 px-3 py-1.5 text-caption dark:border-coffee-700">
        <span className="font-mono text-coffee-700 dark:text-cream-300">{path}</span>
        <span className="text-coffee-500 dark:text-cream-500">
          Edit · <span className="text-green-700 dark:text-green-400">+{addedCount}</span>{" "}
          <span className="text-red-700 dark:text-red-400">−{removedCount}</span>
        </span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-cream-300 font-mono text-body-sm dark:divide-coffee-700">
        <DiffColumn rows={leftRows} side="left" />
        <DiffColumn rows={rightRows} side="right" />
      </div>
    </div>
  );
}
